import {
  PlayOnePager,
  PlayOnePagerColumns,
  PlayOnePagerNotice,
} from "@softmax/product-ux/play";
import { buildHref } from "@softmax/web-addresses";
import { NavigationReady } from "@softmax/web-features/navigation/markers";
import { coworldIconOf } from "@/features/preview/media";
import type { PublicPlayShell } from "../lib/types";
import { divisionLabel } from "../lib/division-label";
import { onePagerCoworldOf, type OnePagerCoworld } from "./coworlds";
import { useOnePagerData } from "./use-one-pager-data";
import { OnePagerHero } from "./one-pager-hero";
import { OnePagerLeaderboard } from "./leaderboard/one-pager-leaderboard";
import { OnePagerPrograms } from "./programs/one-pager-programs";
export function OnePager({
  shell,
  path,
}: {
  readonly shell: PublicPlayShell;
  readonly path: string;
}) {
  const corpus = shell.league.slug;
  const {
    viewer,
    viewerId,
    personal,
    publicBoard,
    data,
    pageShell,
    programs,
    pending,
  } = useOnePagerData({ shell, path });
  let coworld = onePagerCoworldOf(corpus);

  if (!coworld) {
    const art: OnePagerCoworld["art"] = {
      accent: "#945637",
      logo: coworldIconOf(corpus) || undefined,
      still: shell.league.coverArt.stills[0],
    };

    coworld = {
      name: shell.league.gameName,
      links: [
        {
          label: "Docs",
          kind: "page",
          destination: { name: "game.playMarkdown" },
        },
      ],
      art,
    };
  }

  const game = shell.league.route.game;
  const slug = buildHref({ name: "game.home", game }).slice(1);
  return (
    <PlayOnePager
      accent={coworld.art.accent}
      hero={
        <OnePagerHero
          coworld={coworld}
          shell={shell}
          corpus={corpus}
          href={path}
          viewerId={viewerId}
          live={data?.live}
        />
      }
      agentLinks={{
        leaderboard: data?.jsonHref ?? null,
        rules: {
          href: buildHref({ name: "game.playMarkdown", game }),
          label: "/" + slug + "/play.md",
        },
      }}
    >
      {viewer.isError ? (
        <PlayOnePagerNotice
          state={{
            kind: "error",
            message: "Your session could not refresh.",
            pending: viewer.isFetching,
            onRetry: () => void viewer.refetch(),
          }}
        />
      ) : null}
      {personal.isError ? (
        <div style={{ order: -1 }}>
          <NavigationReady stage="personal" path={path} status="error" />
          <PlayOnePagerNotice
            state={{
              kind: "error",
              message: data
                ? "Your programs could not refresh. Showing the last update."
                : "Your programs are unavailable.",
              pending: personal.isFetching,
              onRetry: () => void personal.refetch(),
            }}
          />
        </div>
      ) : null}
      {pending ? (
        <output style={{ order: -1, minHeight: 52 }}>
          Loading your programs…
        </output>
      ) : null}
      <PlayOnePagerColumns
        division={divisionLabel(pageShell.league.divisionName)}
        leaderboard={
          <OnePagerLeaderboard
            personal={data}
            publicBoard={publicBoard}
            path={path}
            refreshPersonal={() => void personal.refetch()}
            refreshingPersonal={personal.isFetching}
          />
        }
        programs={
          <OnePagerPrograms
            data={programs}
            pending={pending}
            shell={pageShell}
            corpus={corpus}
            name={coworld.name}
            path={path}
            viewerId={viewerId}
          />
        }
      />
    </PlayOnePager>
  );
}
